import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Download, FlaskConical, Loader2, Phone } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { DtpResultsSignIn } from '../../components/dtp/DtpResultsSignIn';
import {
  LabAbnormalSummary,
  LabResultTable,
  LabStatusBadge
} from '../../components/labs/LabResultTable';
import { LabResultDetail, LabResultLinkContext } from '../../types/labResults';
import { track } from '../../lib/analytics';

/**
 * A patient reading one of their own lab reports.
 *
 * This is where the link in the results SMS lands. If they already have a
 * session it renders immediately; if not, the sign-in gate takes over IN PLACE,
 * so verifying a code returns them to the exact report they tapped through for
 * rather than to a storefront.
 *
 * The numbers are drawn by the same LabResultTable the clinic sees. What is
 * added here is framing: what the reference range means, that a flag is not a
 * diagnosis, and how to reach the person who ordered the test. A patient
 * reading "Testosterone 1082, High" with no idea whether that is alarming is
 * the outcome this page exists to prevent — and the answer is context and a
 * phone number, not hiding the number.
 */
export function DtpResultDetail() {
  const { physicianId } = useDtpParams<{ physicianId: string }>();
  const { resultId } = useParams<{ resultId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useDtpAuthStore();

  const [result, setResult] = useState<LabResultDetail | null>(null);
  const [linkContext, setLinkContext] = useState<LabResultLinkContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const linkToken = searchParams.get('k');

  // Resolve the link token before sign-in so the gate can name the clinic and
  // hint at the right number. Best-effort — an expired link still reaches an
  // ordinary sign-in, which reaches the same results.
  useEffect(() => {
    if (isAuthenticated || !linkToken) return;
    dtpApi
      .getLabResultLinkContext(linkToken)
      .then(res => {
        if (res.success && res.data) setLinkContext(res.data);
      })
      .catch(() => undefined);
  }, [isAuthenticated, linkToken]);

  const load = React.useCallback(() => {
    if (!resultId || !isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    dtpApi
      .getLabResult(resultId)
      .then(res => {
        if (res.success && res.data) {
          setResult(res.data);
          setError('');
          track('lab_result_viewed', { resultId, flaggedCount: (res.data.observations || []).filter(o => o.isAbnormal).length });
        } else {
          setError(res.error || 'We could not find that result.');
        }
      })
      .catch(() => setError('We could not load your result.'))
      .finally(() => setLoading(false));
  }, [resultId, isAuthenticated]);

  useEffect(() => {
    load();
  }, [load]);

  const openReport = async (index: number) => {
    if (!resultId) return;
    const res = await dtpApi.getLabResultAttachmentUrl(resultId, index);
    if (res.success && res.data?.url) {
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } else {
      setError(res.error || 'We could not open that report.');
    }
  };

  if (!isAuthenticated) {
    return (
      <DtpResultsSignIn
        physicianId={linkContext?.physicianId || physicianId!}
        clinicName={linkContext?.clinicName}
        phoneLast4={linkContext?.phoneLast4}
        onAuthenticated={load}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error && !result) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-red-500" />
        <p className="mt-4 text-gray-700">{error}</p>
        <Link
          to={dtpPath(physicianId, '/results')}
          className="mt-6 inline-block text-sm font-medium text-blue-600"
        >
          See all my results
        </Link>
      </div>
    );
  }

  if (!result) return null;

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <button
        onClick={() => navigate(dtpPath(physicianId, '/results'))}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        All results
      </button>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <FlaskConical className="h-5 w-5 text-gray-400" />
            <h1 className="text-lg font-semibold text-gray-900">{result.panelName}</h1>
            <LabStatusBadge status={result.status} amended={result.amended} />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {formatPatientDate(result)}
            {result.orderingProvider?.lastName
              ? ` · Ordered by ${result.orderingProvider.firstName ?? ''} ${result.orderingProvider.lastName}`.trimEnd()
              : ''}
          </p>
          <div className="mt-3">
            <LabAbnormalSummary
              total={result.observationCount}
              abnormal={result.abnormalCount}
            />
          </div>

          {result.amended && (
            <div className="mt-4 rounded-md bg-purple-50 p-3 text-sm text-purple-800">
              The lab sent an updated version of this report. You are looking at an
              earlier one — open the most recent report from your results list.
            </div>
          )}

          {result.attachments.some(a => a.available) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {result.attachments
                .filter(a => a.available)
                .map(a => (
                  <button
                    key={a.index}
                    onClick={() => openReport(a.index)}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Download className="h-4 w-4" />
                    {a.fileName || "Download the lab's report (PDF)"}
                  </button>
                ))}
            </div>
          )}
        </div>

        {/* Framing, ABOVE the numbers. Someone who opens this page on their phone
            in a car park reads the first screen and little else; the caveat has
            to be there, not at the bottom. */}
        <div className="border-b border-gray-100 bg-blue-50/50 p-5">
          <h2 className="text-sm font-semibold text-gray-900">How to read this</h2>
          <ul className="mt-2 space-y-1.5 text-sm text-gray-700">
            <li>
              The <strong>reference range</strong> is what the lab considers typical for
              most adults. It is not a target, and it is not personalised to you.
            </li>
            <li>
              A marker outside that range is <strong>not automatically a problem</strong>.
              Plenty of results sit outside it for ordinary reasons — including a
              treatment you are already on, which is often the point of the test.
            </li>
            <li>
              Only the clinician who ordered this test can tell you what it means for
              you. Please don't start, stop or change any treatment based on this page.
            </li>
          </ul>
        </div>

        <div className="px-4 py-2 sm:px-5">
          <LabResultTable observations={result.observations} reportNotes={result.reportNotes} />
        </div>

        <div className="border-t border-gray-200 bg-gray-50 p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Phone className="h-4 w-4 text-gray-400" />
            Questions about these results?
          </h2>
          <p className="mt-1.5 text-sm text-gray-600">
            Contact the clinic that ordered the test
            {result.orderingProvider?.lastName
              ? ` — ${result.orderingProvider.firstName ?? ''} ${result.orderingProvider.lastName}`.trimEnd()
              : ''}
            . They have the full report and your history.
          </p>
          <p className="mt-3 text-xs text-gray-500">
            If you feel unwell or this feels urgent, do not wait for a reply — contact
            your doctor or your local emergency number.
          </p>
        </div>
      </div>

      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
    </div>
  );
}

function formatPatientDate(result: { reportedAt?: string; collectedAt?: string; receivedAt?: string }): string {
  const raw = result.reportedAt || result.collectedAt || result.receivedAt;
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

export default DtpResultDetail;
